
import { computed, defineComponent, onMounted, PropType, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// realisations
import { carriers } from '@/core/realisations/carriers.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CarrierT } from '@/core/types/carriers.types'
import { AdditionalServicesWithServicesI } from '@/core/types/additional_services.types'
import { CompleteDataWithServicesPayloadT } from '@/core/types/invoices.types'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { invoices } from '@/core/realisations/invoices.realisation'
import { useRouter } from 'vue-router'

type ServiceData = CompleteDataWithServicesPayloadT & { type_name: string; type_code: string }

export default defineComponent({
    components: {
        modal: Modal,
        'the-input': TheInput,
        'the-select': TheSelect,
        'the-button': TheButton,
    },
    emits: ['cancel', 'submitted', 'deleted'],
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        additionalServices: {
            type: Array as PropType<AdditionalServicesWithServicesI[]>,
            default: () => [],
        },
        invoiceNumber: {
            type: String,
            default: null,
        },
    },
    setup(props, { emit }) {
        const notifications = useNotifications()
        const router = useRouter()

        const state = reactive({
            is_loading: false,
        })

        const payload_state = reactive({
            additionalServices: [] as ServiceData[],
        })

        const data_state = reactive(new DataState<CarrierT>({}))

        //

        const loadCarriers = async (page: number, search?: string) => {
            data_state.pagination.current_page = page

            try {
                data_state.is_loading = true
                const data = await carriers.getList({ page, search, limit: 50, statusId: 2 })

                data_state.list = data.data
                data_state.pagination.total_pages = data.meta.last_page

                data_state.is_failed = false
                data_state.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.failure.catchError(error)

                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_failed = true
                }
            } finally {
                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.pagination.total_pages) return
            data_state.pagination.current_page = page

            try {
                data_state.is_loading = true

                const response = await carriers.getList({ page, statusId: 2 })
                data_state.list = data_state.list.concat(response.data)

                data_state.pagination.total_pages = response.meta.last_page

                data_state.is_failed = false
                data_state.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.failure.catchError(error)

                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_failed = true
                }
            } finally {
                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_loading = false
                }
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.pagination.current_page)
            data_state.list.unshift(carrier)
        }

        //

        const emitCancel = () => {
            emit('cancel')
        }

        const isCanRenderCarrier = (service: { type_code: string }) => {
            return (
                service.type_code !== 'grid' &&
                service.type_code !== 'soft_package' &&
                service.type_code !== 'pallet'
            )
        }

        const initializePayloadState = () => {
            props.additionalServices.forEach((service) => {
                const is_can_render_carrier = isCanRenderCarrier(service)

                if (is_can_render_carrier) {
                    payload_state.additionalServices.push({
                        id: service.id.toString(),
                        type_code: service.type_code,
                        type_name: service.type_name,
                        value: service.value,
                        duration: service.duration,
                        costPerHour: service.cost_per_hour,
                        carrierId: service.carrier_id,
                    })
                } else {
                    payload_state.additionalServices.push({
                        id: service.id.toString(),
                        type_code: service.type_code,
                        type_name: service.type_name,
                        value: service.value,
                        carrierId: null,
                    })
                }
            })
        }

        const validationNotification = (message: string) => {
            notifications.notify({
                type: 'danger',
                title: `Ошибка валидации`,
                description: message,
            })
        }

        const isNumberValid = (value: string | null): boolean => {
            if (!value) return false

            const numered_value = Number(value)
            if (isNaN(numered_value)) return false

            return true
        }

        const isPayloadStateValid = (): boolean => {
            let is_valid = payload_state.additionalServices.some((payload_service) => {
                const is_can_render_carrier = isCanRenderCarrier(payload_service)

                // кран, рохля, грузчики или манипулятор
                if (is_can_render_carrier) {
                    const costPerHour = payload_service.costPerHour as string | null
                    const duration = payload_service.duration as string | null

                    return (
                        payload_service.carrierId &&
                        isNumberValid(payload_service.value) &&
                        isNumberValid(costPerHour) &&
                        isNumberValid(duration)
                    )
                }
                // обрешетка, паллета или мягкая упаковка
                else {
                    return isNumberValid(payload_service.value)
                }
            })

            if (!is_valid) {
                validationNotification('Заполните минимум 1 доп. услугу полностью')
            }

            return is_valid
        }

        const getPayload = () => {
            return payload_state.additionalServices.filter((payload_service) => {
                const is_can_render_carrier = isCanRenderCarrier(payload_service)

                if (is_can_render_carrier) {
                    if (
                        !payload_service.carrierId &&
                        !payload_service.value &&
                        !payload_service.costPerHour &&
                        !payload_service.duration
                    ) {
                        return null
                    }

                    return {
                        id: payload_service.id,
                        carrierId: payload_service.carrierId,
                        value: payload_service.value,
                        costPerHour: payload_service.costPerHour,
                        duration: payload_service.duration,
                    }
                }
            })
        }

        const emitSubmitted = () => {
            if (!isPayloadStateValid()) return

            const additionalServices = getPayload()
            emit('submitted', { additionalServices })
        }

        const deleteService = async (id: number) => {
            const answer = confirm(`Вы действительно хотите удалить доп. услугу?`)
            if (!answer) return

            try {
                state.is_loading = true

                await invoices.deleteService(id)

                notifications.notify({
                    title: 'Доп. услуга удалена',
                    type: 'success',
                })

                emit('deleted')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При удалении произошла ошбика',
                })
            } finally {
                state.is_loading = false
            }
        }

        const addWriteOff = async (service: AdditionalServicesWithServicesI) => {
            if (!props.invoiceNumber || !service.type_code) return

            router.push(
                `/tmc/write-offs/create?invoice_number=${props.invoiceNumber}&service_type=${service.type_code}`
            )
        }

        onMounted(() => {
            loadCarriers(1)
            initializePayloadState()
        })

        const formClasses = computed(() => {
            if (props.additionalServices.length < 3) {
                return 'grid grid-cols-1 gap-4'
            }

            return 'grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-4'
        })

        const isConfirmDisabled = computed(() => {
            return payload_state.additionalServices.every((payload_service) => {
                return !isCanRenderCarrier(payload_service)
            })
        })

        const isWriteOff = (service: AdditionalServicesWithServicesI) => {
            return !isCanRenderCarrier(service) && !service.value
        }

        return {
            state,
            data_state,
            payload_state,
            //
            deleteService,
            addWriteOff,
            emitCancel,
            emitSubmitted,
            isCanRenderCarrier,
            isWriteOff,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            //
            formClasses,
            isConfirmDisabled,
        }
    },
})
